/**
 * Home v1 8-product grid data per spec §3.5.
 *
 * Order derives from HOME_PRODUCT_ORDER (productOrder.ts) — the single source
 * of truth for product sequence across nav and grid. Forge Coach is nav-only
 * and not included here. `num` is derived from position, not stored per card.
 *
 * Lede strings support **bold** markup via surrounding asterisks — the
 * ProductGrid component parses this into <strong> spans per spec's copy rule
 * ("bold 1 emphasis phrase per product lede"). Keep ledes ≤ 12 words.
 */

import { HOME_PRODUCT_ORDER, HomeProductPath, PRODUCT_NAMES } from "./productOrder";
import { Localized } from "../utils/localized";

export type HomeProductChip = "live" | "soon" | "hero" | "free" | "proven" | "standalone";

export interface HomeProduct {
  num: string;
  role: string;
  title: string;
  lede: string;
  chip: HomeProductChip;
  features: string[];
  routeLabel: string;
  href: string;
  featured?: boolean;
}

// Card content keyed by route. Order is NOT defined here — it is projected
// from HOME_PRODUCT_ORDER below, and `num` is derived from position.
//
// Localized PER CARD (not as a separate hardcoded es array) so `en` and `es`
// structurally cannot drift: both HOME_PRODUCTS.en and .es below are derived
// from the same HOME_PRODUCT_ORDER.map() loop, so reordering/adding/removing
// a product in productOrder.ts propagates to both languages automatically.
type HomeProductCard = Omit<HomeProduct, "num">;

const CARDS_BY_HREF: Record<HomeProductPath, Localized<HomeProductCard>> = {
  "/claude-harness": {
    en: {
      role: "Configuration",
      title: "The Production Harness for Claude Code",
      lede: "**Configure the environment your agents run in** — CLAUDE.md, skills, hooks, unattended runs",
      chip: "standalone",
      features: [
        "Free: score your own harness on 8 dimensions",
        "6 modules, ~22 hours — Claude Code configuration and hooks",
        "One-time purchase — or included with All-Access",
      ],
      routeLabel: "See the course",
      href: "/claude-harness",
    },
    es: {
      role: "Configuración",
      title: "The Production Harness for Claude Code",
      lede: "**Configura el entorno donde corren tus agentes** — CLAUDE.md, skills, hooks, ejecuciones desatendidas",
      chip: "standalone",
      features: [
        "Gratis: evalúa tu propio harness en 8 dimensiones",
        "6 módulos, ~22 horas — configuración de Claude Code y hooks",
        "Pago único — o incluido con All-Access",
      ],
      routeLabel: "Ver el curso",
      href: "/claude-harness",
    },
  },

  "/career-transition": {
    en: {
      role: "Orchestrator",
      title: "Career Transition",
      lede: "15-question diagnostic → your **personalized roadmap**",
      chip: "hero",
      features: [
        "6 transition frameworks",
        "Routes into every product",
        "Claude-powered diagnostic",
      ],
      routeLabel: "Your plan",
      href: "/career-transition",
      featured: true,
    },
    es: {
      role: "Orquestador",
      title: "Career Transition",
      lede: "Diagnóstico de 15 preguntas → tu **hoja de ruta personalizada**",
      chip: "hero",
      features: [
        "6 marcos de transición",
        "Conecta con todos los productos",
        "Diagnóstico impulsado por Claude",
      ],
      routeLabel: "Tu plan",
      href: "/career-transition",
      featured: true,
    },
  },
  "/agentic-ai-mastery": {
    en: {
      role: "Foundation",
      title: "Agentic AI Mastery",
      lede: "**Six-module curriculum** on agentic architecture, Anthropic standards",
      chip: "live",
      features: [
        "6 modules, ~30 hours total",
        "Multi-agent system patterns",
        "Production engineering + safety",
      ],
      routeLabel: "Open course",
      href: "/agentic-ai-mastery",
    },
    es: {
      role: "Fundamento",
      title: "Agentic AI Mastery",
      lede: "**Currículo de seis módulos** sobre arquitectura agéntica y estándares de Anthropic",
      chip: "live",
      features: [
        "6 módulos, ~30 horas en total",
        "Patrones de sistemas multiagente",
        "Ingeniería de producción + seguridad",
      ],
      routeLabel: "Abrir curso",
      href: "/agentic-ai-mastery",
    },
  },
  "/agentic-ops": {
    en: {
      role: "Operations",
      title: "Agentic Ops",
      lede: "**Operate the agents you can't watch** — ops companion to Mastery",
      chip: "live",
      features: [
        "5 modules, ~23 hours",
        "Unattended loops + earned autonomy",
        "Verification walls + cost routing",
      ],
      routeLabel: "Open course",
      href: "/agentic-ops",
    },
    es: {
      role: "Operaciones",
      title: "Agentic Ops",
      lede: "**Opera los agentes que no puedes vigilar** — complemento de operaciones de Mastery",
      chip: "live",
      features: [
        "5 módulos, ~23 horas",
        "Bucles desatendidos + autonomía ganada",
        "Muros de verificación + enrutamiento de costes",
      ],
      routeLabel: "Abrir curso",
      href: "/agentic-ops",
    },
  },
  "/portfolio-kit": {
    en: {
      role: "Evidence",
      title: "Portfolio Kit",
      lede: "Three **project archetypes** with eval harnesses + Lovable prompt kit",
      chip: "live",
      features: [
        "7-domain evidence mapping",
        "README + deployment templates",
        "Honest-fit portfolio builder",
      ],
      routeLabel: "Open kit",
      href: "/portfolio-kit",
    },
    es: {
      role: "Evidencia",
      title: "Portfolio Kit",
      lede: "Tres **arquetipos de proyecto** con arneses de evaluación + kit de prompts de Lovable",
      chip: "live",
      features: [
        "Mapeo de evidencia en 7 dominios",
        "Plantillas de README + despliegue",
        "Constructor de portafolio de ajuste honesto",
      ],
      routeLabel: "Abrir kit",
      href: "/portfolio-kit",
    },
  },
  "/resume-mastery": {
    en: {
      role: "Positioning",
      title: "Resume Mastery",
      lede: "**AI tailors your résumé** to any role — company-specific, server-side",
      chip: "live",
      features: [
        "AI gap analysis + targeted rewrites",
        "Company-specific exemplars",
        "Free ATS score vs any JD",
      ],
      routeLabel: "Open Resume Mastery",
      href: "/resume-mastery",
    },
    es: {
      role: "Posicionamiento",
      title: "Resume Mastery",
      lede: "**La IA adapta tu currículo** a cualquier puesto — específico por empresa, del lado del servidor",
      chip: "live",
      features: [
        "Análisis de brechas con IA + reescrituras dirigidas",
        "Ejemplos específicos por empresa",
        "Puntuación ATS gratuita frente a cualquier JD",
      ],
      routeLabel: "Abrir Resume Mastery",
      href: "/resume-mastery",
    },
  },
  "/agentic-interviewing": {
    en: {
      role: "Prep",
      title: PRODUCT_NAMES.agenticAiInterviewing,
      lede: "**The technical interview, decoded** — 4 progressive levels, hiring-manager calibrated",
      chip: "live",
      features: [
        "Level 1 free, levels 2–4 gated",
        "Scenario + system design drills",
        "Hiring-manager calibration",
      ],
      routeLabel: "Open playbook",
      href: "/agentic-interviewing",
    },
    es: {
      role: "Preparación",
      title: PRODUCT_NAMES.agenticAiInterviewing,
      lede: "**La entrevista técnica, descifrada** — 4 niveles progresivos, calibrados por responsables de contratación",
      chip: "live",
      features: [
        "Nivel 1 gratuito, niveles 2–4 restringidos",
        "Ejercicios de escenarios + diseño de sistemas",
        "Calibración por responsables de contratación",
      ],
      routeLabel: "Abrir playbook",
      href: "/agentic-interviewing",
    },
  },
  "/linkedin-presence": {
    en: {
      role: "Visibility",
      title: "LinkedIn Presence",
      lede: "Profile prompts, **post archetypes**, and outreach templates for 4 personas",
      chip: "live",
      features: [
        "Profile optimization prompts",
        "Post archetypes with exemplars",
        "Monthly analytics + outreach",
      ],
      routeLabel: "Open builder",
      href: "/linkedin-presence",
    },
    es: {
      role: "Visibilidad",
      title: "LinkedIn Presence",
      lede: "Prompts de perfil, **arquetipos de publicaciones** y plantillas de contacto para 4 perfiles",
      chip: "live",
      features: [
        "Prompts de optimización de perfil",
        "Arquetipos de publicaciones con ejemplos",
        "Analíticas mensuales + contacto",
      ],
      routeLabel: "Abrir constructor",
      href: "/linkedin-presence",
    },
  },
  "/coach": {
    en: {
      role: "Practice",
      title: "AI Interview Coach",
      lede: "**Where strong candidates stumble** — AI-scored STAR practice, 12× more likely to pass",
      chip: "proven",
      features: [
        "6-dimension evaluation",
        "Company-specific alignment",
        "Mock + generator + question bank",
      ],
      routeLabel: "Open coach",
      href: "/coach",
      featured: true,
    },
    es: {
      role: "Práctica",
      title: "AI Interview Coach",
      lede: "**Donde tropiezan los candidatos fuertes** — práctica STAR evaluada por IA, 12× más probabilidades de aprobar",
      chip: "proven",
      features: [
        "Evaluación en 6 dimensiones",
        "Alineación específica por empresa",
        "Simulacro + generador + banco de preguntas",
      ],
      routeLabel: "Abrir coach",
      href: "/coach",
      featured: true,
    },
  },
  "/visibility-strategy": {
    en: {
      role: "Diagnostic",
      title: "Visibility Strategy Workshop",
      lede: "**30-min free workshop** on the 4 failure points that keep AI/ML candidates invisible",
      chip: "free",
      features: [
        "4 failure points identified",
        "No login required",
        "30-minute format",
      ],
      routeLabel: "Start workshop",
      href: "/visibility-strategy",
    },
    es: {
      role: "Diagnóstico",
      title: "Visibility Strategy Workshop",
      lede: "**Taller gratuito de 30 min** sobre los 4 puntos de fallo que mantienen invisibles a los candidatos de IA/ML",
      chip: "free",
      features: [
        "4 puntos de fallo identificados",
        "No requiere inicio de sesión",
        "Formato de 30 minutos",
      ],
      routeLabel: "Iniciar taller",
      href: "/visibility-strategy",
    },
  },
};

// Single source for ORDER (HOME_PRODUCT_ORDER) and for the NUMBER (position).
// HOME_PRODUCT_ORDER itself is locale-invariant routing config (href strings
// only, no prose) — it stays unwrapped. Both en and es below project from the
// SAME loop over the SAME order array, so they cannot structurally drift.
export const HOME_PRODUCTS: Localized<HomeProduct[]> = {
  en: HOME_PRODUCT_ORDER.map((href, i) => ({
    ...CARDS_BY_HREF[href].en,
    num: String(i + 1).padStart(2, "0"),
  })),
  es: HOME_PRODUCT_ORDER.map((href, i) => ({
    ...(CARDS_BY_HREF[href].es ?? CARDS_BY_HREF[href].en),
    num: String(i + 1).padStart(2, "0"),
  })),
};
